import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, CheckCircle2, Award, Cloud, Zap, Shield, BarChart3, Users, Target, Clock, Sparkles, TrendingUp, Lock, Headphones, ExternalLink, ChevronRight, Building2, Workflow, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const HomePageTest = () => {
  const [formData, setFormData] = useState({ name: '', email: '', goal: '' });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Premium Two-Column */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Ambient background blobs */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-brand-100/30 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-mint-100/20 rounded-full blur-[100px] translate-x-1/2" />
        
        {/* Subtle grain overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' /%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge line with gold accent */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-mist rounded-full border border-line mb-8">
                <span className="text-xs font-medium text-ink/70">SAP Business One Premier Partner</span>
                <div className="w-1 h-1 rounded-full bg-gold-400" />
                <span className="text-xs font-medium text-ink/70">AWS Advanced Partner</span>
              </div>

              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-ink mb-6 leading-[1.08] tracking-tight">
                Business Software.<br />
                <span className="text-brand-500">Built to Scale with You.</span>
              </h1>

              <p className="text-xl text-ink/60 mb-10 leading-relaxed max-w-xl">
                SAP Business One on AWS. Complete visibility, confident decisions, and room to grow. Built specifically for ambitious SMEs.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <Button className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white shadow-sm px-6 py-3 font-medium transition-all duration-200 focus:ring-4 focus:ring-brand-200">
                  Book a consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" className="rounded-xl border border-line bg-white hover:bg-brand-50 px-6 py-3 font-medium transition-all duration-200 focus:ring-4 focus:ring-brand-200">
                  See customer outcomes
                </Button>
              </div>

              {/* Tag chips */}
              <div className="flex flex-wrap gap-2">
                {['Implementation', 'AWS Hosting', 'Managed Services', 'Reporting & Automation'].map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-xs font-medium ring-1 ring-brand-100">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Right: Featured Media Frame */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative"
            >
              {/* Featured frame with gradient stroke */}
              <div className="rounded-2xl bg-gradient-to-b from-white to-mist p-[1px]">
                <div className="rounded-2xl bg-white p-6">
                  <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=90" 
                    alt="SAP Business One Dashboard"
                    className="rounded-xl w-full h-auto shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)]"
                  />
                  
                  {/* Mini stat tiles */}
                  <div className="grid grid-cols-3 gap-3 mt-6">
                    {[
                      { icon: TrendingUp, label: 'Revenue', indicator: '↑' },
                      { icon: Clock, label: 'Efficiency', indicator: '↑' },
                      { icon: CheckCircle2, label: 'Uptime', indicator: '◎' }
                    ].map((stat, i) => (
                      <div key={i} className="p-4 bg-mist rounded-xl border border-line">
                        <stat.icon className="w-4 h-4 text-brand-500 mb-2" />
                        <div className="text-xs text-ink/50 mb-1">{stat.label}</div>
                        <div className="text-2xl font-bold text-ink">{stat.indicator}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Authority Strip - Premium Credential Cards */}
      <section className="py-16 bg-gradient-to-b from-mist/50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* SAP Premier - Gold tinted */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="rounded-2xl bg-gradient-to-br from-gold-50 to-white p-[1px] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]">
                <div className="rounded-2xl bg-white p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gold-50 flex items-center justify-center">
                      <Award className="w-6 h-6 text-gold-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-ink/50">SAP Business One</div>
                      <div className="text-lg font-semibold text-ink">Premier Partner</div>
                    </div>
                  </div>
                  <p className="text-sm text-ink/60 leading-relaxed">15+ years delivering enterprise ERP excellence to growing businesses worldwide</p>
                </div>
              </div>
            </motion.div>

            {/* AWS Advanced - Brand tinted */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group"
            >
              <div className="rounded-2xl bg-gradient-to-br from-brand-50 to-white p-[1px] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]">
                <div className="rounded-2xl bg-white p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center">
                      <Cloud className="w-6 h-6 text-brand-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-ink/50">AWS</div>
                      <div className="text-lg font-semibold text-ink">Advanced Partner</div>
                    </div>
                  </div>
                  <p className="text-sm text-ink/60 leading-relaxed">Certified cloud infrastructure expertise with 99.995% uptime SLA guarantee</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bento Outcomes Grid */}
      <section id="solutions" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-mist rounded-full border border-line mb-4">
                <Sparkles className="w-3.5 h-3.5 text-brand-500" />
                <span className="text-xs font-medium text-ink/70">Measurable Outcomes</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-ink mb-4 tracking-tight">Business Control That Grows with You</h2>
              <p className="text-lg text-ink/60 max-w-2xl mx-auto">Real capabilities that help SMEs compete and scale without the complexity or cost of legacy systems</p>
            </motion.div>
          </div>

          {/* Bento grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Large tile - Featured */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 lg:row-span-2 group"
            >
              <div className="h-full rounded-2xl border border-line bg-white p-8 hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]">
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-mint-50 text-mint-700 rounded-md text-xs font-medium ring-1 ring-mint-100 mb-4">
                  <CheckCircle2 className="w-3 h-3" />
                  Real Integration
                </div>
                <h3 className="text-2xl font-bold text-ink mb-4">Finance, Sales & Stock - All Connected</h3>
                <p className="text-ink/60 mb-8 leading-relaxed">Real-time visibility across your whole business. One system, one version of the truth, better decisions.</p>
                <div className="space-y-3">
                  {['Live view of what\'s happening now', 'Stop chasing spreadsheets for reports', 'See patterns and make better calls', 'Know what to order and when'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-brand-500" />
                      <span className="text-sm text-ink/70">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Outcome tile 1 */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group"
            >
              <div className="h-full rounded-2xl border border-line bg-white p-8 hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]">
                <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                  <TrendingUp className="w-5 h-5 text-brand-500" />
                </div>
                <div className="text-4xl font-bold text-ink mb-2">284%</div>
                <div className="text-sm text-ink/60">Average customer ROI</div>
              </div>
            </motion.div>

            {/* Outcome tile 2 - Gold highlight */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="group"
            >
              <div className="h-full rounded-2xl bg-gradient-to-br from-gold-50 to-white p-[1px]">
                <div className="h-full rounded-2xl bg-white p-8 hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]">
                  <div className="w-10 h-10 rounded-lg bg-gold-50 flex items-center justify-center mb-4">
                    <Award className="w-5 h-5 text-gold-600" />
                  </div>
                  <div className="text-4xl font-bold text-ink mb-2">99.995%</div>
                  <div className="text-sm text-ink/60">AWS uptime SLA</div>
                </div>
              </div>
            </motion.div>

            {/* Visual tile */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="lg:row-span-2"
            >
              <div className="h-full rounded-2xl border border-line bg-mist p-6 overflow-hidden">
                <div className="rounded-xl overflow-hidden border border-line bg-white">
                  <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=85" 
                    alt="Analytics"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            {/* Testimonial tile */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25 }}
              className="md:col-span-2 lg:col-span-1"
            >
              <div className="h-full rounded-2xl border border-line bg-white p-8 hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]">
                <p className="text-sm text-ink/70 italic mb-4 leading-relaxed">"Ascarii gave us complete operational clarity. We now make data-driven decisions across all subsidiaries."</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-100" />
                  <div>
                    <div className="text-sm font-semibold text-ink">Hill & Smith Group</div>
                    <div className="text-xs text-ink/50">Manufacturing</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Outcome tile 3 */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group"
            >
              <div className="h-full rounded-2xl border border-line bg-white p-8 hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]">
                <div className="w-10 h-10 rounded-lg bg-mint-50 flex items-center justify-center mb-4">
                  <Zap className="w-5 h-5 text-mint-600" />
                </div>
                <div className="text-4xl font-bold text-ink mb-2">8-12</div>
                <div className="text-sm text-ink/60">Weeks to go-live</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Chapter 1: Implementation */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-xs font-medium ring-1 ring-brand-100 mb-6">
                <Target className="w-3 h-3" />
                Implementation
              </div>
              <h2 className="text-4xl font-bold text-ink mb-6 tracking-tight">SAP Business One for Your Business</h2>
              <p className="text-lg text-ink/60 mb-8 leading-relaxed max-w-prose">Configured for how you work. Deployed without disrupting your operations. Supported by people who understand growing businesses.</p>
              <div className="space-y-4">
                {[
                  'We learn how your business works, not force you into a template',
                  'Configure SAP B1 to match your processes and workflows',
                  'Train your team so they\'re confident from day one'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-brand-600" />
                    </div>
                    <span className="text-ink/70">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl border border-line bg-mist p-4">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=85" 
                  alt="Implementation"
                  className="rounded-xl w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Chapter 2: AWS Hosting */}
      <section className="py-24 bg-gradient-to-b from-mist/30 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="rounded-2xl border border-line bg-mist p-4">
                <img 
                  src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=85" 
                  alt="Cloud Infrastructure"
                  className="rounded-xl w-full h-auto"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-mint-50 text-mint-700 rounded-full text-xs font-medium ring-1 ring-mint-100 mb-6">
                <Cloud className="w-3 h-3" />
                AWS Hosting
              </div>
              <h2 className="text-4xl font-bold text-ink mb-6 tracking-tight">Reliable Cloud Infrastructure</h2>
              <p className="text-lg text-ink/60 mb-8 leading-relaxed max-w-prose">Your data secure and always accessible. AWS hosting means you never worry about servers, backups, or downtime.</p>
              <div className="space-y-4">
                {[
                  '99.995% uptime guarantee - your business always running',
                  'Automatic backups every day - your data is safe',
                  'Bank-level security without the bank-level cost'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-mint-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-mint-600" />
                    </div>
                    <span className="text-ink/70">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Chapter 3: Managed Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-xs font-medium ring-1 ring-brand-100 mb-6">
                <Headphones className="w-3 h-3" />
                Managed Services
              </div>
              <h2 className="text-4xl font-bold text-ink mb-6 tracking-tight">We're Here When You Need Us</h2>
              <p className="text-lg text-ink/60 mb-8 leading-relaxed max-w-prose">Proactive support that keeps your system running smoothly. Real people who understand your business, not a call center.</p>
              <div className="space-y-4">
                {[
                  'Proactive monitoring catches issues before you notice',
                  'Regular check-ins and optimization as your business evolves',
                  'Fast response when you need help - we know your setup'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-brand-600" />
                    </div>
                    <span className="text-ink/70">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl border border-line bg-mist p-4">
                <img 
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=85" 
                  alt="Support"
                  className="rounded-xl w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section id="cases" className="py-24 bg-gradient-to-b from-mist/30 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-mist rounded-full border border-line mb-4">
                <Award className="w-3.5 h-3.5 text-brand-500" />
                <span className="text-xs font-medium text-ink/70">Customer Success</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-ink mb-4 tracking-tight">Measurable Business Outcomes</h2>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                company: 'Hill & Smith Group', 
                industry: 'Manufacturing', 
                outcome: 'Unified operations across 12 companies after acquisition', 
                metrics: ['Multi-company', 'M&A ready'],
                featured: true
              },
              { 
                company: 'Hawthorn Trading', 
                industry: 'Wholesale Distribution', 
                outcome: '£13k saved in Q1 with better purchasing decisions', 
                metrics: ['Cost savings', 'Data clarity']
              },
              { 
                company: 'Abbeydale Direct', 
                industry: 'E-commerce', 
                outcome: 'Scaled 10x without outgrowing their system', 
                metrics: ['Growth', 'Scalability']
              }
            ].map((caseStudy, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group"
              >
                <div className={`h-full rounded-2xl ${caseStudy.featured ? 'bg-gradient-to-br from-gold-50 to-white p-[1px]' : 'border border-line bg-white'} hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]`}>
                  <div className={`${caseStudy.featured ? 'rounded-2xl bg-white' : ''} p-8`}>
                    <div className="text-xs font-medium text-brand-600 mb-3">{caseStudy.industry}</div>
                    <h3 className="text-xl font-semibold text-ink mb-3">{caseStudy.company}</h3>
                    <p className="text-sm text-ink/70 mb-6 leading-relaxed">{caseStudy.outcome}</p>
                    <div className="flex flex-wrap gap-2">
                      {caseStudy.metrics.map((tag, j) => (
                        <span key={j} className="px-2 py-1 bg-mist text-ink/60 rounded-md text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-ink mb-4 tracking-tight">Our Partnership Process</h2>
              <p className="text-lg text-ink/60">Proven methodology ensuring predictable outcomes</p>
            </motion.div>
          </div>

          <div className="space-y-6">
            {[
              { phase: 'Discover', desc: 'Understand your requirements and map existing workflows', icon: Target },
              { phase: 'Design', desc: 'Configure SAP B1 to match your business processes', icon: BarChart3 },
              { phase: 'Deliver', desc: 'Smooth implementation with comprehensive training', icon: Send },
              { phase: 'Optimize', desc: 'Continuous improvement and proactive support', icon: TrendingUp }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative"
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-brand-500 flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 pt-1">
                    <h3 className="text-xl font-semibold text-ink mb-2">{step.phase}</h3>
                    <p className="text-ink/60 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
                {i < 3 && (
                  <div className="absolute left-6 top-12 bottom-0 w-px bg-gradient-to-b from-brand-200 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing / Service Packages */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-mist/30 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-ink mb-4 tracking-tight">Service Packages</h2>
              <p className="text-lg text-ink/60">Flexible options designed for your needs</p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { 
                name: 'Implementation', 
                desc: 'Get SAP B1 set up right for your business',
                features: ['We learn how you work', 'Configure to your needs', 'Train your team properly', 'Support through go-live'],
                highlight: false
              },
              { 
                name: 'Managed Services', 
                desc: 'Ongoing support so you can focus on your business',
                features: ['We keep it running smoothly', 'Proactive monitoring', 'Fast help when needed', 'Regular optimization'],
                highlight: true,
                badge: 'Most Popular'
              },
              { 
                name: 'AWS Cloud Hosting', 
                desc: 'Reliable hosting without the IT headaches',
                features: ['99.995% uptime promise', 'Automatic daily backups', 'Secure and compliant', 'Scales with you'],
                highlight: false
              }
            ].map((tier, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group"
              >
                <div className={`h-full rounded-2xl ${tier.highlight ? 'bg-gradient-to-br from-gold-50 to-white p-[1px]' : 'border border-line bg-white'} hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]`}>
                  <div className={`${tier.highlight ? 'rounded-2xl bg-white' : ''} p-8`}>
                    {tier.badge && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-gold-50 text-gold-700 rounded-md text-xs font-medium ring-1 ring-gold-100 mb-4">
                        <Award className="w-3 h-3" />
                        {tier.badge}
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-ink mb-2">{tier.name}</h3>
                    <p className="text-sm text-ink/60 mb-8 leading-relaxed">{tier.desc}</p>
                    <ul className="space-y-3 mb-8">
                      {tier.features.map((feature, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-brand-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-ink/70">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-medium py-2.5 transition-all duration-200">
                      Learn more
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Panel */}
      <section className="py-24 bg-gradient-to-b from-white to-mist">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="rounded-2xl border border-line bg-white p-12 shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)]">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold-50 text-gold-700 rounded-full text-xs font-medium ring-1 ring-gold-100 mb-6">
                  <Award className="w-3 h-3" />
                  Premier Partner Credentials
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-ink mb-4 tracking-tight">Talk to an Expert</h2>
                <p className="text-lg text-ink/60 max-w-xl mx-auto">Get started with SAP Business One on AWS. Book a consultation with our specialists.</p>
              </div>

              <form className="space-y-5 max-w-md mx-auto">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-ink/70 mb-2">Full name</label>
                  <input 
                    id="name"
                    type="text" 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full h-11 px-4 rounded-lg border border-line bg-white focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-ink/70 mb-2">Work email</label>
                  <input 
                    id="email"
                    type="email" 
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full h-11 px-4 rounded-lg border border-line bg-white focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="goal" className="block text-sm font-medium text-ink/70 mb-2">Primary goal</label>
                  <input 
                    id="goal"
                    type="text" 
                    value={formData.goal}
                    onChange={(e) => setFormData({...formData, goal: e.target.value})}
                    className="w-full h-11 px-4 rounded-lg border border-line bg-white focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200"
                    placeholder="e.g., Cloud migration, New implementation"
                  />
                </div>
                <Button type="submit" className="w-full rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-medium py-3 transition-all duration-200 focus:ring-4 focus:ring-brand-200">
                  Request consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Premium Footer */}
      <footer className="py-16 bg-white border-t border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <img src="/ascarii-logo.png" alt="Ascarii" className="h-7 mb-4" />
              <p className="text-sm text-ink/60 leading-relaxed max-w-sm">
                SAP Business One Premier Partner and AWS Advanced Partner delivering enterprise ERP with cloud clarity.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink mb-4">Solutions</h4>
              <ul className="space-y-2">
                {['Implementation', 'AWS Hosting', 'Managed Services', 'Support'].map((item, i) => (
                  <li key={i}>
                    <a href="#" className="text-sm text-ink/60 hover:text-brand-700 transition-colors duration-200">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink mb-4">Company</h4>
              <ul className="space-y-2">
                {['About', 'Case Studies', 'Blog', 'Contact'].map((item, i) => (
                  <li key={i}>
                    <a href="#" className="text-sm text-ink/60 hover:text-brand-700 transition-colors duration-200">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-line flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-xs text-ink/50">© 2025 Ascarii. All rights reserved.</div>
            <div className="flex gap-6">
              <a href="/privacy-policy" className="text-xs text-ink/50 hover:text-brand-700 transition-colors duration-200">Privacy</a>
              <a href="/terms" className="text-xs text-ink/50 hover:text-brand-700 transition-colors duration-200">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePageTest;
